import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDown, Check, CircleDot, Eye, Flag, RotateCw } from 'lucide-react';

const stages = [
  {
    key: 'reason',
    icon: CircleDot,
    step: 'Reason',
    title: 'Understand the work',
    body: 'Read the schedule, patient context, ledger, communications, and operating rules together.',
    status: 'Context ready',
  },
  {
    key: 'act',
    icon: RotateCw,
    step: 'Act',
    title: 'Carry out the next step',
    body: 'Complete the authorized action inside the PMS and coordinate the next dependent task.',
    status: 'In progress',
  },
  {
    key: 'observe',
    icon: Eye,
    step: 'Observe',
    title: 'Verify and continue',
    body: 'Confirm the result, update shared context, and either continue the run or route an exception.',
    status: 'Monitoring',
  },
];

const workStates = [
  {
    label: 'Queued',
    tone: 'neutral',
    Icon: CircleDot,
    detail: 'Work enters the run with its context attached',
  },
  {
    label: 'In progress',
    tone: 'running',
    Icon: RotateCw,
    detail: 'Authorized actions move through the operating loop',
  },
  {
    label: 'Needs review',
    tone: 'review',
    Icon: Flag,
    detail: 'An exception pauses with a clear decision attached',
  },
  {
    label: 'Completed',
    tone: 'completed',
    Icon: Check,
    detail: 'The result and next state enter the activity trail',
  },
];

export default function AutopilotRun({ compact = false }) {
  const reduceMotion = useReducedMotion();
  const [activeStateIndex, setActiveStateIndex] = useState(0);
  const visibleStateIndex = reduceMotion ? workStates.length - 1 : activeStateIndex;
  const currentState = workStates[visibleStateIndex];

  useEffect(() => {
    if (reduceMotion || activeStateIndex >= workStates.length - 1) return undefined;

    const timer = window.setTimeout(() => {
      setActiveStateIndex((index) => Math.min(index + 1, workStates.length - 1));
    }, 1800);

    return () => window.clearTimeout(timer);
  }, [activeStateIndex, reduceMotion]);

  return (
    <section
      className={`alt4-autopilot-run${compact ? ' alt4-autopilot-run--compact' : ''}`}
      aria-labelledby="autopilot-run-title"
    >
      <header className="alt4-autopilot-run__header">
        <div>
          <span className="alt4-interface-label">Live task run</span>
          <h2 id="autopilot-run-title">Schedule readiness · Tomorrow</h2>
        </div>
        <motion.span
          className={`alt4-status alt4-status--${currentState.tone}`}
          data-testid="autopilot-current-status"
          aria-live="polite"
          animate={reduceMotion ? undefined : { opacity: [0.72, 1], y: [2, 0] }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          <currentState.Icon aria-hidden="true" /> {currentState.label}
        </motion.span>
      </header>

      <div className="alt4-autopilot-run__goal">
        <Flag aria-hidden="true" />
        <div>
          <span>Operating goal</span>
          <strong>Prepare every scheduled visit for a clean handoff</strong>
        </div>
      </div>

      <ol className="alt4-autopilot-run__stages" aria-label="Autopilot reason, act, observe loop">
        {stages.map(({ key, icon: Icon, step, title, body, status }, index) => (
          <React.Fragment key={key}>
            <li className={`alt4-autopilot-run__stage alt4-autopilot-run__stage--${key}`}>
              <span className="alt4-autopilot-run__icon"><Icon aria-hidden="true" /></span>
              <div>
                <span className="alt4-interface-label">{step}</span>
                <h3>{title}</h3>
                <p>{body}</p>
                <span className="alt4-status">{status}</span>
              </div>
            </li>
            {index < stages.length - 1 && <ArrowDown className="alt4-autopilot-run__arrow" aria-hidden="true" />}
          </React.Fragment>
        ))}
      </ol>

      <div className="alt4-autopilot-run__controls" aria-label="Work status lifecycle">
        {workStates.map(({ label, tone, Icon, detail }, index) => {
          const isCurrent = index === visibleStateIndex;
          return (
            <motion.div
              className={`alt4-run-control alt4-run-control--${tone}${isCurrent ? ' is-current' : ''}`}
              key={label}
              aria-current={isCurrent ? 'step' : undefined}
              animate={
                reduceMotion
                  ? undefined
                  : { opacity: isCurrent ? 1 : 0.62, scale: isCurrent ? 1 : 0.985 }
              }
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <Icon aria-hidden="true" />
              <span>
                <strong>
                  <span className={`alt4-status alt4-status--${tone}`} data-autopilot-state>
                    {label}
                  </span>
                </strong>
                {detail}
              </span>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
